import {NullMessageId, type CopilotChatRepo, type FileReference} from '@github-ui/copilot-chat-types'
import type {
  File,
  PreviewableContent,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Reuse the existing workbench item contract until Chat V1 is retired.
} from '@github-ui/copilot-immersive-v1/components/ContentPreview/content-preview-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {MAX_ATTACHMENT_SIZE_BYTES} from '@github-ui/copilot-shared-ui/attachments/process-uploaded-files'
import {verifiedFetch, verifiedFetchJSON} from '@github-ui/verified-fetch'

export type WorkbenchPreviewSource =
  | {
      type: 'repository-file'
      repo: Pick<CopilotChatRepo, 'id' | 'ownerLogin' | 'name'>
      path: string
      ref: string
      commitOID?: string
      startLine?: number
      endLine?: number
    }
  | {type: 'uploaded-file'; id: string; name: string; url?: string; content?: string; mimeType?: string}
  | {type: 'image'; id: string; name: string; alt?: string; url: string; mimeType?: string}

export const MAX_PREVIEW_BYTES = MAX_ATTACHMENT_SIZE_BYTES
const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg', 'bmp', 'ico'])
const binaryExtensions = new Set([
  'pdf',
  'zip',
  'gz',
  'tar',
  '7z',
  'exe',
  'dll',
  'wasm',
  'woff',
  'woff2',
  'ttf',
  'mp3',
  'mp4',
  'mov',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
])

function origin() {
  return ssrSafeLocation.origin || 'https://github.com'
}

export function safePreviewUrl(value: string | undefined): string | undefined {
  if (!value) return
  try {
    const url = new URL(value, origin())
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return
    if (url.username || url.password) return
    if (
      url.origin !== origin() &&
      !(
        url.protocol === 'https:' &&
        (url.hostname === 'githubusercontent.com' || url.hostname.endsWith('.githubusercontent.com'))
      )
    )
      return
    return url.href
  } catch {
    return
  }
}

function sourceName(source: WorkbenchPreviewSource) {
  return source.type === 'repository-file' ? source.path : source.name
}

function uploadedImageBlob(value: string | undefined): Blob | undefined {
  if (!value || value.length > MAX_ATTACHMENT_SIZE_BYTES * 1.4) return
  try {
    const url = new URL(value, origin())
    if (url.protocol !== 'data:') return
    const comma = url.pathname.indexOf(',')
    const media = url.pathname.slice(0, comma).toLowerCase()
    if (
      !['image/png;base64', 'image/jpeg;base64', 'image/gif;base64', 'image/webp;base64', 'image/avif;base64'].includes(
        media,
      )
    )
      return
    const bytes = Uint8Array.from(atob(url.pathname.slice(comma + 1)), character => character.charCodeAt(0))
    if (bytes.byteLength > MAX_ATTACHMENT_SIZE_BYTES) return
    return new Blob([bytes], {type: media.split(';')[0]})
  } catch {
    return
  }
}

export function previewSourceIdentity(source: WorkbenchPreviewSource): string {
  return encodeURIComponent(
    JSON.stringify(
      source.type === 'repository-file'
        ? [
            source.repo.ownerLogin.toLowerCase(),
            source.repo.name.toLowerCase(),
            source.repo.id,
            source.ref,
            source.commitOID || '',
            source.path,
          ]
        : ['upload', source.id],
    ),
  )
}

function repositoryReference(source: Extract<WorkbenchPreviewSource, {type: 'repository-file'}>): FileReference {
  const revision = source.commitOID || source.ref
  const path = source.path.split('/').map(encodeURIComponent).join('/')
  return {
    type: 'file',
    path: source.path,
    repoID: source.repo.id,
    repoOwner: source.repo.ownerLogin,
    repoName: source.repo.name,
    ref: source.ref,
    commitOID: source.commitOID || '',
    url: new URL(
      `/${encodeURIComponent(source.repo.ownerLogin)}/${encodeURIComponent(source.repo.name)}/blob/${encodeURIComponent(revision)}/${path}`,
      origin(),
    ).href,
  }
}

function sourceUrl(source: WorkbenchPreviewSource): string | undefined {
  if (source.type !== 'repository-file') return safePreviewUrl(source.url)
  const reference = repositoryReference(source)
  return reference.url.replace('/blob/', '/raw/')
}

function repositoryResourceResolver(
  source: Extract<WorkbenchPreviewSource, {type: 'repository-file'}>,
): NonNullable<File['resolveReferenceId']> {
  const url = new URL(repositoryReference(source).url, origin())
  const revisionRoot = `/${encodeURIComponent(source.repo.ownerLogin)}/${encodeURIComponent(source.repo.name)}/blob/${encodeURIComponent(source.commitOID || source.ref)}/`
  return reference => {
    try {
      const resourceUrl = new URL(reference, url)
      if (resourceUrl.origin !== url.origin || !resourceUrl.pathname.startsWith(revisionRoot)) return null
      const path = resourceUrl.pathname.slice(revisionRoot.length).split('/').map(decodeURIComponent).join('/')
      return `file:source-${previewSourceIdentity({...source, path})}`
    } catch {
      return null
    }
  }
}

function fileItem(source: WorkbenchPreviewSource): File {
  return {
    id: `file:source-${previewSourceIdentity(source)}#0`,
    type: 'file',
    name: sourceName(source),
    messageId: NullMessageId,
    value: '',
    language: '',
    timestamp: new Date(),
    isUserEdited: false,
    isStreaming: false,
    readOnly: true,
    sourceUrl: sourceUrl(source),
    mimeType: source.type === 'repository-file' ? undefined : source.mimeType,
    reference: source.type === 'repository-file' ? repositoryReference(source) : undefined,
    resolveReferenceId: source.type === 'repository-file' ? repositoryResourceResolver(source) : undefined,
    range:
      source.type === 'repository-file' && source.startLine
        ? {start: source.startLine, end: source.endLine ?? source.startLine}
        : undefined,
  }
}

export function unavailablePreview(source: WorkbenchPreviewSource, reason: string): File {
  return {...fileItem(source), unavailableReason: reason}
}

export function isBinary(name: string, mimeType?: string) {
  const extension = name.split('.').at(-1)?.toLowerCase() ?? ''
  if (binaryExtensions.has(extension)) return true
  if (!mimeType) return false
  const mime = mimeType.split(';')[0]?.toLowerCase() ?? ''
  return (
    !mime.startsWith('text/') &&
    !['application/json', 'application/javascript', 'application/xml', 'image/svg+xml'].includes(mime)
  )
}

export async function loadWorkbenchPreview(
  source: WorkbenchPreviewSource,
  signal?: AbortSignal,
): Promise<PreviewableContent> {
  if (source.type === 'repository-file' && !source.ref && !source.commitOID) {
    return unavailablePreview(source, 'The repository revision is unavailable.')
  }
  const name = sourceName(source)
  const extension = name.split('.').at(-1)?.toLowerCase() ?? ''
  const mimeType = source.type === 'repository-file' ? undefined : source.mimeType
  if (source.type === 'image' || mimeType?.startsWith('image/') || imageExtensions.has(extension)) {
    const url = sourceUrl(source)
    const blob = !url && source.type !== 'repository-file' ? uploadedImageBlob(source.url) : undefined
    if (!url && !blob) return unavailablePreview(source, 'This image does not have a supported preview URL.')
    return {
      type: 'image',
      id: `image:source-${previewSourceIdentity(source)}`,
      name,
      altText: source.type === 'image' ? source.alt || name : name,
      url: url ?? '',
      blob,
      mimeType: mimeType || blob?.type,
      messageId: NullMessageId,
      reference: source.type === 'repository-file' ? repositoryReference(source) : undefined,
    }
  }
  if (isBinary(name, mimeType)) {
    return unavailablePreview(
      source,
      'Preview is unavailable for this file format. Download the original file instead.',
    )
  }
  const item = fileItem(source)
  if (source.type === 'repository-file') {
    const revision = encodeURIComponent(source.commitOID || source.ref)
    const path = source.path.split('/').map(encodeURIComponent).join('/')
    const response = await verifiedFetchJSON(
      `/github-copilot/chat/file_contents/${source.repo.id}/${revision}/${path}`,
      {signal},
    )
    if (!response.ok)
      return unavailablePreview(source, 'Unable to load this file. It may no longer exist or you may not have access.')
    const payload: unknown = await response.json()
    if (!payload || typeof payload !== 'object' || !('content' in payload) || typeof payload.content !== 'string') {
      return unavailablePreview(source, 'File contents are unavailable.')
    }
    item.value = payload.content
    item.language = 'language' in payload && typeof payload.language === 'string' ? payload.language : ''
    if (['plaintext', 'text'].includes(item.language.toLowerCase())) item.language = ''
  } else if (source.type === 'uploaded-file' && source.content !== undefined) {
    item.value = source.content
  } else {
    const url = sourceUrl(source)
    // Never send authenticated requests to arbitrary attachment URLs.
    if (!url || new URL(url, origin()).origin !== origin())
      return unavailablePreview(source, 'File contents are unavailable.')
    const response = await verifiedFetch(url, {signal})
    if (!response.ok) return unavailablePreview(source, 'Unable to load this uploaded file.')
    item.mimeType = response.headers.get('content-type') || item.mimeType
    const isImage = item.mimeType?.startsWith('image/')
    if (Number(response.headers.get('content-length')) > (isImage ? MAX_ATTACHMENT_SIZE_BYTES : MAX_PREVIEW_BYTES)) {
      return unavailablePreview(source, 'This file is too large to preview. Download the original file instead.')
    }
    if (isImage) return loadWorkbenchPreview({...source, mimeType: item.mimeType}, signal)
    if (isBinary(name, item.mimeType)) return unavailablePreview(source, 'Preview is unavailable for this file format.')
    item.value = await response.text()
  }
  if (item.value.includes('\0')) return unavailablePreview(source, 'Preview is unavailable for this binary file.')
  if (item.value.length > MAX_PREVIEW_BYTES || new Blob([item.value]).size > MAX_PREVIEW_BYTES) {
    return unavailablePreview(source, 'This file is too large to preview. Download the original file instead.')
  }
  return item
}
